import React, {FC, useEffect, useMemo, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {addressService} from "../../../store/actions";
import {getFiat, serializeQuery, toXDCNumber, toXDCNumberWithoutFormat} from "../../../common/utils/function";
import Popup from 'reactjs-popup';
import BEmpty from "../../../components/b-empty";
import useCommon from "../../../common/hooks/useCommon";
import useDebouncedEffect from "use-debounced-effect";
import {useSelector} from "react-redux";
import BTooltip from "../../../components/b-tooltip";
import TokenImage from "../../common/token-image";

interface AccountTransactionData {
    hasTokens?: boolean
}

interface TransactionListData {
    token: any;
    name: string;
    quantity: string;
}


const AccountTransaction: FC<AccountTransactionData> = (props) => {
    const {address} = useParams();
    const _tokenType = useSelector((e: Object) => (e as any)?.common?.tokenType);

    const [search, setSearch] = useState('')
    const [transactionList, setTransactionList] = useState<TransactionListData[]>([]);
    const [groupList, setGroupList] = useState({xrc20: [], xrc404: [], xrc721: [], xrc1155: []});
    const [tokenLength, setTokenLength] = useState<any>("")

    const totalBalance = useMemo(() => {
        let total = 0
        transactionList.forEach((e: any) => {
            total += toXDCNumberWithoutFormat(e?.value, e?.token?.decimals) * Number((e?.token?.fiat_value ?? e?.token?.exchange_rate) ?? 0)
        })
        return total ? getFiat((total ?? 0), 18, "USD", true) : ""
    }, [transactionList]);

    useEffect(() => {
        if (props?.hasTokens) {
            addressService.getAddressTokens(address, serializeQuery({type: "ERC-20"})).then(response => {
                setTransactionList(prevState => [...prevState, ...response?.items])
                setGroupList(prevState => ({...prevState, xrc20: response?.items}))
                if (response?.items?.length >= 50) setTokenLength('>')
            }).catch((e) => '');
            addressService.getAddressTokens(address, serializeQuery({type: "ERC-404"})).then(response => {
                setTransactionList(prevState => [...prevState, ...response?.items])
                setGroupList(prevState => ({...prevState, xrc404: response?.items}))
                if (response?.items?.length >= 50) setTokenLength('>')
            }).catch((e) => '');
            addressService.getAddressTokens(address, serializeQuery({type: "ERC-721"})).then(response => {
                setTransactionList(prevState => [...prevState, ...response?.items])
                setGroupList(prevState => ({...prevState, xrc721: response?.items}))
                if (response?.items?.length >= 50) setTokenLength('>')
            }).catch((e) => '');
            addressService.getAddressTokens(address, serializeQuery({type: "ERC-1155"})).then(response => {
                setTransactionList(prevState => [...prevState, ...response?.items])
                setGroupList(prevState => ({...prevState, xrc1155: response?.items}))
                if (response?.items?.length >= 50) setTokenLength('>')
            }).catch((e) => '');
        }
    }, [address, props?.hasTokens]);

    useDebouncedEffect(() => {
        let localList = JSON.parse(JSON.stringify(transactionList))
        if (search) localList = transactionList.filter((item) => search.toLowerCase().split(" ").every((v: any) => item.token.name.toLowerCase().includes(v)));
        setGroupList({
            xrc20: localList.filter((e: any) => e?.token.type === "ERC-20") ?? [],
            xrc404: localList.filter((e: any) => e?.token.type === "ERC-404") ?? [],
            xrc721: localList.filter((e: any) => e?.token.type === "ERC-721") ?? [],
            xrc1155: localList.filter((e: any) => e?.token.type === "ERC-1155") ?? [],
        })
    }, 1000, [search]);
    return <React.Fragment>
        {props?.hasTokens ?
            <Popup
                trigger={<span className="bs-address-token">
                    <span>{tokenLength}{totalBalance} ({tokenLength}{transactionList?.length} Tokens) {transactionList?.length > 0 && <BTooltip
                        text={`More than ${transactionList?.length} tokens found, listing and displaying the total balance of the first 100 tokens only. Click on the Wallet icon to see the full list and balance.`}><i
                        className="bi bi-question-circle me-2"></i></BTooltip>}
                    </span>
                    <i className="bi bi-chevron-down"></i>
                </span>}
                position="bottom left"
                arrow={false}
                className="bs-address-token"
            >
                <div>
                    <div className="bs-address-token-header">
                        <input className="form-control" placeholder="Search For Token Name" value={search} onChange={e => setSearch(e?.target?.value)}/>
                    </div>
                    <div className="bs-address-token-body">
                        <div className="bs-badge h-gray mb-1">{_tokenType}-20 Tokens ({groupList?.xrc20?.length >= 50 ? '>' : ''}{groupList?.xrc20?.length ?? 0})</div>
                        <ul>
                            {groupList?.xrc20?.map((data: any, i) => {
                                return <ListItem
                                    key={i}
                                    token={data?.token?.address}
                                    symbol={data?.token?.symbol}
                                    name={data?.token?.name}
                                    icon_url={data?.token?.icon_url}
                                    decimals={data?.token?.decimals}
                                    value={data?.value}
                                    fiat_value={data?.token?.fiat_value ?? data?.token?.exchange_rate}
                                />
                            })}
                            {groupList?.xrc20?.length === 0 && <BEmpty style={{minHeight: 'unset'}}/>}
                        </ul>
                        <div className="bs-badge h-gray mb-1">{_tokenType}-404 Tokens ({groupList?.xrc404?.length ?? 0})</div>
                        <ul>
                            {groupList?.xrc404?.map((data: any, i) => {
                                return <ListItem
                                    key={i}
                                    token={data?.token?.address}
                                    symbol={data?.token?.symbol}
                                    name={data?.token?.name}
                                    icon_url={data?.token?.icon_url}
                                    decimals={data?.token?.decimals}
                                    value={data?.value}
                                    fiat_value={data?.token?.fiat_value ?? data?.token?.exchange_rate}
                                />
                            })}
                            {groupList?.xrc404?.length === 0 && <BEmpty style={{minHeight: 'unset'}}/>}
                        </ul>
                        <div className="bs-badge h-gray my-2">{_tokenType}-721 Tokens ({groupList?.xrc721?.length ?? 0})</div>
                        <ul>
                            {groupList?.xrc721?.map((data: any, i) => {
                                return <ListItem
                                    key={i}
                                    token={data?.token?.address}
                                    symbol={data?.token?.symbol}
                                    name={data?.token?.name}
                                    decimals={data?.token?.decimals}
                                    value={data?.value}
                                    fiat_value={data?.token?.fiat_value ?? data?.token?.exchange_rate}
                                />
                            })}
                            {groupList?.xrc721?.length === 0 && <BEmpty style={{minHeight: 'unset'}}/>}
                        </ul>
                        <div className="bs-badge h-gray my-2">{_tokenType}-1155 Tokens ({groupList?.xrc1155?.length ?? 0})</div>
                        <ul>
                            {groupList?.xrc1155?.map((data: any, i) => {
                                return <ListItem
                                    key={i}
                                    token={data?.token?.address}
                                    symbol={data?.token?.symbol}
                                    name={data?.token?.name}
                                    decimals={data?.token?.decimals}
                                    value={data?.value}
                                    fiat_value={data?.token?.fiat_value ?? data?.token?.exchange_rate}
                                />
                            })}
                            {groupList?.xrc1155?.length === 0 && <BEmpty style={{minHeight: 'unset'}}/>}
                        </ul>
                    </div>
                </div>

            </Popup> :
            <span className="bs-address-token">
                <span>0 Token</span>
                <i className="bi bi-chevron-down"></i>
            </span>}
    </React.Fragment>
};

interface ListItemData {
    token?: any;
    symbol?: any;
    icon_url?: any;
    name?: any;
    decimals?: any;
    value?: any;
    fiat_value?: any;
}

const ListItem: FC<ListItemData> = (props) => {
    const {address} = useParams();
    const {get0x} = useCommon();
    return <li>
        <Link to={`/token/${get0x(props?.token)}?address=${address}`}>
            <div>
                <p className="d-flex align-items-center">
                    <TokenImage image={props?.icon_url} symbol={props?.symbol} params={{width: 16, className: "me-1"}}/>
                    <span>{props?.name} ({props?.symbol})</span>
                </p>
                <p className="gray">
                    {toXDCNumber(props?.value, props?.decimals, 4)}
                </p>
            </div>
            <div>
                <p className="gray"> {props?.fiat_value ? getFiat(toXDCNumberWithoutFormat(props?.value, props?.decimals) * Number(props?.fiat_value ?? 1), 18, "USD", true) : "-"}</p>
                <p className="gray text-end">{props?.fiat_value ? getFiat(props?.fiat_value ?? 0, 4) : "-"}</p>
            </div>
        </Link>
    </li>
}
export default AccountTransaction;
